/**
 * Plan audit panel — on the plan governance page, beside the Authorize button
 * it informs. PRD: docs/9-exploration/PRD-plan-audit.md.
 *
 * Read-only and never blocking: it lists what a careful reviewer would catch by
 * hand, each with a link to where it is fixed. The links go to pages that make
 * their own permission checks.
 */
import { Link } from 'react-router-dom';
import { ClipboardCheck, CheckCircle } from 'lucide-react';
import { usePlanAudit } from '../../hooks/usePlanAudit';
import { useScopedLink } from '../../lib/itemLinkScope';
import { PLAN_AUDIT_CHECK_LABELS, PLAN_AUDIT_CHECK_ORDER } from '../../lib/planAuditCore';

export function PlanAuditPanel({
  planVersionId,
  versionNumber,
  workspaceId,
}: {
  planVersionId: string | null;
  versionNumber: number | null;
  workspaceId: string | null;
}) {
  const { loading, error, result } = usePlanAudit(planVersionId, workspaceId);
  const scopedLink = useScopedLink();
  if (!planVersionId) return null;

  return (
    <section className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden mb-6" aria-label="Plan audit">
      <div className="px-4 py-3 border-b border-[var(--rule)] bg-[var(--surface)]">
        <h2 className="font-semibold text-[var(--text-pri)] flex items-center gap-2">
          <ClipboardCheck className="h-4 w-4 text-[var(--text-ter)]" />
          Plan audit{versionNumber != null ? ` — version ${versionNumber}` : ''}
        </h2>
        <p className="text-sm text-[var(--text-sec)] mt-0.5">
          Duplicates, work with no owner or objective, commitments with no reason, unlike things ranked together,
          commitments the Cascade did not accept, and commitments outside what the cycle set out to do.
          This informs; it does not stop authorization.
        </p>
      </div>

      {loading && <div className="px-4 py-4 text-sm text-[var(--text-ter)]">Checking this version…</div>}

      {!loading && error && (
        <div className="px-4 py-4 text-sm text-[var(--coral)]">The audit could not read this version: {error}</div>
      )}

      {!loading && result && (
        <div className="px-4 py-3 space-y-4">
          {result.checkedItems > 0 && (
            <ul aria-label="Items each check found" className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-[var(--text-ter)]">
              {PLAN_AUDIT_CHECK_ORDER.map((check) => {
                const n = result.counts[check];
                return (
                  <li key={check}>
                    {PLAN_AUDIT_CHECK_LABELS[check]}:{' '}
                    <span className={n ? 'font-semibold text-[var(--text-pri)] tabular-nums' : 'tabular-nums'}>
                      {n === null ? 'not run' : n}
                    </span>
                  </li>
                );
              })}
            </ul>
          )}
          {result.notRun.length > 0 && (
            <ul className="space-y-0.5 text-xs text-[var(--text-ter)]">
              {result.notRun.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
          {result.findings.length === 0 ? (
            <p className="flex items-center gap-2 text-sm text-[var(--text-sec)]">
              <CheckCircle className="h-4 w-4 text-[var(--green)]" />
              {result.checkedItems === 0
                ? 'There are no items on this version to check.'
                : `Nothing found across ${result.checkedItems} item${result.checkedItems === 1 ? '' : 's'}.`}
            </p>
          ) : (
            PLAN_AUDIT_CHECK_ORDER.map((check) => {
              const findings = result.findings.filter((f) => f.check === check);
              if (findings.length === 0) return null;
              return (
                <div key={check}>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] mb-1.5">
                    {PLAN_AUDIT_CHECK_LABELS[check]}
                  </h3>
                  <ul className="space-y-2">
                    {findings.map((f) => (
                      <li key={f.id} className="text-sm text-[var(--text-pri)]">
                        <p>{f.sentence}</p>
                        {f.links.length > 0 && (
                          <p className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-xs">
                            {f.links.map((l) => (
                              <Link key={`${f.id}:${l.href}`} to={scopedLink(l.href)} className="text-primary-600 hover:underline">
                                {l.label}
                              </Link>
                            ))}
                          </p>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })
          )}
          {result.readFrom.length > 0 && (
            <ul className="border-t border-[var(--rule)] pt-2 space-y-0.5 text-xs text-[var(--text-ter)]">
              {result.readFrom.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
